import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { usePaged, ShowMore } from "@/lib/usePaged";
import { PersonalPerformance } from "@/components/PersonalPerformance";
import { WarrantyTasks } from "@/components/WarrantyTasks";
import { CrmTasks } from "@/components/CrmTasks";
import { SalesTargets } from "@/components/SalesTargets";
import { PipelineFunnel } from "@/components/PipelineFunnel";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can, isCentralRole, canDeleteRecord, canEditRecord } from "@/lib/rbac";
import { useConfig } from "@/lib/useConfig";
import { LeadBadge, PageHead, EmptyState } from "@/components/Bits";
import { Badge } from "@/components/ui/badge";
import { ExportButtons } from "@/components/ExportButtons";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const LEAD_SOURCES = [
  "Customer Referral",
  "Central Sales Team",
  "Instagram",
  "Google Map",
  "Walk-in",
  "Other",
];

const EXPORT_COLUMNS = [
  { label: "Name", value: (c) => c.name },
  { label: "Phone", value: (c) => c.phone },
  { label: "Email", value: (c) => c.email },
  { label: "Make", value: (c) => c.vehicle?.make },
  { label: "Model", value: (c) => c.vehicle?.model },
  { label: "Year", value: (c) => c.vehicle?.year },
  { label: "Colour", value: (c) => c.vehicle?.colour },
  { label: "VIN", value: (c) => c.vehicle?.vin },
  { label: "Lead source", value: (c) => c.lead_source },
  { label: "Sub-source", value: (c) => c.sub_source },
  { label: "Studio", value: (c) => c.studio_name || "Unassigned" },
];

const EMPTY = {
  name: "",
  phone: "",
  email: "",
  lead_source: "",
  sub_source: "",
  notes: "",
  cs_person_id: "",
  vehicle: { make: "", model: "", year: "", colour: "", vin: "", plate: "" },
};

const isCentralSalesSource = (s) => (s || "").toLowerCase().includes("central sales");

export default function Customers() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [lead, setLead] = useState("all");
  const [stage, setStage] = useState("all");
  const [studios, setStudios] = useState([]);
  const [studioId, setStudioId] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const isAdmin = !!user.is_network;
  const isCentral = isCentralRole(user.role);
  const canWrite = can(user, "customers:write") && (!!user.studio_id || !!user.is_network);
  const canDelete = can(user, "customers:delete");
  const [delTarget, setDelTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editId, setEditId] = useState(null);
  const SUB_LIST = ["Direct Call", "Instagram", "WhatsApp Message", "WhatsApp API", "Walk-in", "Referral", "Other"];
  const leadSourceOptions = useConfig("lead-sources");
  const stageOptions = useConfig("pipeline-stages");
  const STAGE_LIST = stageOptions.map((s) => s.name);
  const brands = useConfig("brands");
  const [csTeam, setCsTeam] = useState([]);
  const LEAD_LIST = leadSourceOptions.length
    ? leadSourceOptions.map((l) => l.name)
    : LEAD_SOURCES;

  useEffect(() => {
    api
      .get("/users", { params: { role: "central_sales_team" } })
      .then(({ data }) => setCsTeam((data || []).filter((u) => u.active !== false)))
      .catch(() => setCsTeam([]));
  }, []);

  const load = useCallback(() => {
    const params = {};
    if (search) params.search = search;
    if (lead !== "all") params.lead_source = lead;
    if (stage !== "all") params.stage = stage;
    if (isAdmin && studioId !== "all") params.studio_id = studioId;
    api.get("/customers", { params }).then(({ data }) => setRows(data));
  }, [search, lead, stage, studioId, isAdmin]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    if (isAdmin) api.get("/studios").then(({ data }) => setStudios(data));
  }, [isAdmin]);

  const submit = async () => {
    try {
      const payload = {
        ...form,
        cs_person_id: isCentralSalesSource(form.lead_source) && form.cs_person_id
          ? form.cs_person_id
          : null,
        vehicle: { ...form.vehicle, year: form.vehicle.year ? Number(form.vehicle.year) : null },
      };
      if (editId) {
        await api.put(`/customers/${editId}`, payload);
        toast.success("Customer updated");
      } else {
        await api.post("/customers", payload);
        toast.success("Customer added");
      }
      setOpen(false);
      setForm(EMPTY);
      setEditId(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const openEdit = (c) => {
    setForm({
      name: c.name || "",
      phone: c.phone || "",
      email: c.email || "",
      lead_source: c.lead_source || "",
      sub_source: c.sub_source || "",
      notes: c.notes || "",
      cs_person_id: c.cs_person_id || "",
      studio_id: c.studio_id || "",
      vehicle: {
        make: c.vehicle?.make || "",
        model: c.vehicle?.model || "",
        year: c.vehicle?.year || "",
        colour: c.vehicle?.colour || "",
        vin: c.vehicle?.vin || "",
        plate: c.vehicle?.plate || "",
      },
    });
    setEditId(c.id);
    setOpen(true);
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setV = (k) => (e) =>
    setForm((f) => ({ ...f, vehicle: { ...f.vehicle, [k]: e.target.value } }));

  const paged = usePaged(rows);

  const doDelete = async () => {
    if (!delTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/customers/${delTarget.id}`);
      toast.success(`${delTarget.name} deleted`);
      setDelTarget(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      {["central_sales_team", "crm"].includes(user.role) && <PersonalPerformance />}
      {["central_sales_team"].includes(user.role) && (
        <div className="mb-6 grid gap-6 lg:grid-cols-2" data-testid="customers-sales-target">
          <SalesTargets />
          <PipelineFunnel variant="job-mine" />
        </div>
      )}
      {user.role === "crm" && (
        <>
          <CrmTasks />
          <div className="mb-6">
            <WarrantyTasks />
          </div>
        </>
      )}
      <PageHead title="Customers" subtitle={isAdmin ? "All studios" : user.studio_name}>
        <ExportButtons
          testidPrefix="customers"
          filename="veloskin-customers"
          title="Customer Report"
          subtitle={isAdmin ? "All studios" : user.studio_name}
          columns={EXPORT_COLUMNS}
          rows={rows}
        />
        {canWrite && (
          <Dialog
            open={open}
            onOpenChange={(v) => {
              setOpen(v);
              if (!v) {
                setForm(EMPTY);
                setEditId(null);
              }
            }}
          >
            <DialogTrigger asChild>
              <Button data-testid="add-customer-btn" onClick={() => { setEditId(null); setForm(EMPTY); }}>
                <Plus className="mr-2 h-4 w-4" /> Add customer
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>{editId ? "Edit customer" : "New customer"}</DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Label>Name *</Label>
                  <Input data-testid="customer-name-input" value={form.name} onChange={set("name")} />
                </div>
                <div>
                  <Label>Phone *</Label>
                  <Input data-testid="customer-phone-input" value={form.phone} onChange={set("phone")} />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input data-testid="customer-email-input" value={form.email} onChange={set("email")} />
                </div>
                {isAdmin && (
                  <div className="sm:col-span-2">
                    <Label>Studio (optional — can be set when the sale closes)</Label>
                    <Select
                      value={form.studio_id || ""}
                      onValueChange={(v) => setForm((f) => ({ ...f, studio_id: v }))}
                    >
                      <SelectTrigger className="min-h-[44px]" data-testid="customer-studio-select">
                        <SelectValue placeholder="Assign a studio later" />
                      </SelectTrigger>
                      <SelectContent>
                        {studios.map((st) => (
                          <SelectItem key={st.id} value={st.id}>
                            {st.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="sm:col-span-2">
                  <Label>Lead source{isCentral ? "" : " (optional at lead stage)"}</Label>
                  {isCentral ? (
                    <Input
                      data-testid="customer-lead-source-locked"
                      className="mt-1 bg-slate-50 text-slate-500"
                      value="Central Sales Team"
                      readOnly
                    />
                  ) : (
                    <Select
                      value={form.lead_source}
                      onValueChange={(v) => setForm((f) => ({ ...f, lead_source: v }))}
                    >
                      <SelectTrigger data-testid="customer-lead-source-select">
                        <SelectValue placeholder="How was this customer acquired?" />
                      </SelectTrigger>
                      <SelectContent>
                        {LEAD_LIST.map((s) => (
                          <SelectItem key={s} value={s} data-testid={`lead-option-${s}`}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                {!isCentral && isCentralSalesSource(form.lead_source) && (
                  <div className="sm:col-span-2">
                    <Label>Central Sales Team B2C member (optional)</Label>
                    <Select
                      value={form.cs_person_id || "__none__"}
                      onValueChange={(v) =>
                        setForm((f) => ({ ...f, cs_person_id: v === "__none__" ? "" : v }))
                      }
                    >
                      <SelectTrigger data-testid="customer-cs-person-select" className="mt-1">
                        <SelectValue placeholder="Assign a specific person (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__" data-testid="cs-person-option-none">
                          Not assigned yet
                        </SelectItem>
                        {csTeam.map((m) => (
                          <SelectItem key={m.id} value={m.id} data-testid={`cs-person-option-${m.id}`}>
                            {m.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {csTeam.length === 0 && (
                      <p className="mt-1 text-xs text-slate-400" data-testid="cs-person-empty">
                        No active Central Sales Team B2C members yet.
                      </p>
                    )}
                  </div>
                )}
                {isCentral && (
                  <div className="sm:col-span-2">
                    <Label>How did this lead come in? (optional at lead stage)</Label>
                    <Select
                      value={form.sub_source}
                      onValueChange={(v) => setForm((f) => ({ ...f, sub_source: v }))}
                    >
                      <SelectTrigger data-testid="customer-sub-source-select">
                        <SelectValue placeholder="Select sub-source" />
                      </SelectTrigger>
                      <SelectContent>
                        {SUB_LIST.map((s) => (
                          <SelectItem key={s} value={s} data-testid={`sub-source-option-${s}`}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label>Make (optional at lead stage)</Label>
                  <Select
                    value={form.vehicle.make}
                    onValueChange={(v) =>
                      setForm((f) => ({ ...f, vehicle: { ...f.vehicle, make: v, model: "" } }))
                    }
                  >
                    <SelectTrigger data-testid="vehicle-make-input">
                      <SelectValue placeholder="Select brand" />
                    </SelectTrigger>
                    <SelectContent>
                      {brands.map((b) => (
                        <SelectItem key={b.id} value={b.name}>
                          {b.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Model (optional at lead stage)</Label>
                  <Select
                    value={form.vehicle.model}
                    onValueChange={(v) =>
                      setForm((f) => ({ ...f, vehicle: { ...f.vehicle, model: v } }))
                    }
                  >
                    <SelectTrigger data-testid="vehicle-model-input">
                      <SelectValue placeholder="Select model" />
                    </SelectTrigger>
                    <SelectContent>
                      {(brands.find((b) => b.name === form.vehicle.make)?.models || []).map((m) => (
                        <SelectItem key={m} value={m}>
                          {m}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Year</Label>
                  <Input data-testid="vehicle-year-input" value={form.vehicle.year} onChange={setV("year")} />
                </div>
                <div>
                  <Label>Colour</Label>
                  <Input data-testid="vehicle-colour-input" value={form.vehicle.colour} onChange={setV("colour")} />
                </div>
                <div>
                  <Label>VIN</Label>
                  <Input data-testid="vehicle-vin-input" value={form.vehicle.vin} onChange={setV("vin")} />
                </div>
                <div>
                  <Label>Plate</Label>
                  <Input data-testid="vehicle-plate-input" value={form.vehicle.plate} onChange={setV("plate")} />
                </div>
              </div>
              <DialogFooter>
                <Button
                  data-testid="save-customer-btn"
                  onClick={submit}
                  disabled={!form.name || !form.phone}
                >
                  {editId ? "Save changes" : "Save customer"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageHead>

      <div className="mb-4 flex flex-wrap gap-3">
        <div className="relative min-w-[240px] flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            data-testid="customer-search-input"
            className="pl-9"
            placeholder="Search name, phone or vehicle…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={lead} onValueChange={setLead}>
          <SelectTrigger className="w-[200px]" data-testid="lead-source-filter">
            <SelectValue placeholder="Lead source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All lead sources</SelectItem>
            {LEAD_LIST.map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {STAGE_LIST.length > 0 && (
          <Select value={stage} onValueChange={setStage}>
            <SelectTrigger className="w-[200px]" data-testid="pipeline-stage-filter">
              <SelectValue placeholder="Pipeline stage" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All pipeline stages</SelectItem>
              {STAGE_LIST.map((s) => (
                <SelectItem key={s} value={s} data-testid={`stage-option-${s}`}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {isAdmin && (
          <Select value={studioId} onValueChange={setStudioId}>
            <SelectTrigger className="w-[220px]" data-testid="studio-filter">
              <SelectValue placeholder="Studio" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All studios</SelectItem>
              {studios.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <Card className="border-brand-line shadow-none">
        <CardContent className="overflow-x-auto p-0">
          <Table data-testid="customers-table" className="mobile-cards">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Vehicle</TableHead>
                <TableHead>VIN</TableHead>
                <TableHead>Stage</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Studio</TableHead>
                {(canWrite || canDelete) && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={(canWrite || canDelete) ? 8 : 7} className="p-0">
                    <EmptyState
                      icon={Plus}
                      testid="customers-empty-state"
                      title="No customers yet"
                      hint={
                        canWrite
                          ? "Add your first customer to start building the pipeline — quotes, jobs and warranties all hang off the customer record."
                          : "Once studios start adding customers they will appear here."
                      }
                      action={
                        canWrite ? (
                          <Button data-testid="customers-empty-add-btn" onClick={() => setOpen(true)}>
                            <Plus className="mr-2 h-4 w-4" /> Add your first customer
                          </Button>
                        ) : null
                      }
                    />
                  </TableCell>
                </TableRow>
              )}
              {paged.visible.map((c) => {
                const isCS = (c.lead_source || "").toLowerCase().includes("central sales");
                const member = c.assigned_to || c.created_by || "Central Sales Team";
                return (
                  <TableRow key={c.id} className="hover:bg-brand-tint/60">
                    <TableCell data-label="Customer" className="font-medium">
                      <Link
                        data-testid={`customer-link-${c.id}`}
                        to={`/customers/${c.id}`}
                        className="hover:text-crimson-600"
                      >
                        {c.name}
                      </Link>
                    </TableCell>
                    <TableCell data-label="Phone" className="text-slate-500">{c.phone}</TableCell>
                    <TableCell data-label="Vehicle" className="text-slate-700">
                      {c.vehicle.make} {c.vehicle.model} {c.vehicle.year ? `· ${c.vehicle.year}` : ""}
                    </TableCell>
                    <TableCell data-label="VIN" className="font-mono text-xs text-slate-500">{c.vehicle.vin}</TableCell>
                    <TableCell data-label="Stage" data-testid={`customer-stage-${c.id}`}>
                      {c.pipeline_stage ? (
                        <Badge className="border-0 bg-brand-tint text-brand">{c.pipeline_stage}</Badge>
                      ) : (
                        <span className="text-xs italic text-slate-400">No job yet</span>
                      )}
                    </TableCell>
                    <TableCell data-label="Source" data-testid={`customer-source-${c.id}`}>
                      {isCS ? (
                        <div className="space-y-0.5">
                          <span
                            className="block font-medium text-brand-ink"
                            data-testid={`customer-cs-member-${c.id}`}
                          >
                            {member}
                          </span>
                          <span className="block text-xs text-slate-400">Central Sales Team</span>
                          {c.sub_source && (
                            <span
                              className="block text-xs text-slate-500"
                              data-testid={`customer-sub-source-${c.id}`}
                            >
                              via {c.sub_source}
                            </span>
                          )}
                        </div>
                      ) : (
                        <LeadBadge source={c.lead_source} />
                      )}
                    </TableCell>
                    <TableCell data-label="Studio" data-testid={`customer-studio-${c.id}`}>
                      {c.studio_name ? (
                        <span className="text-slate-700">{c.studio_name}</span>
                      ) : (
                        <span className="text-xs italic text-slate-400">Unassigned</span>
                      )}
                    </TableCell>
                    {(canWrite || canDelete) && (
                      <TableCell data-label="Actions" className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {canEditRecord(user, c, "customers:write") && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-9 w-9 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                              data-testid={`edit-customer-${c.id}`}
                              onClick={() => openEdit(c)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                          {canDeleteRecord(user, c, "customers:delete") && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-9 w-9 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                              data-testid={`delete-customer-${c.id}`}
                              onClick={() => setDelTarget(c)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <ShowMore paged={paged} testid="customers-show-more" />
        </CardContent>
      </Card>

      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent className="sm:max-w-md" data-testid="delete-customer-dialog">
          <DialogHeader>
            <DialogTitle>Delete customer?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            This permanently deletes <strong>{delTarget?.name}</strong> along with their quotes and
            interaction history. Customers that still have jobs can't be deleted — reassign or
            delete those jobs first. This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDelTarget(null)}>
              Cancel
            </Button>
            <Button
              className="bg-rose-600 text-white hover:bg-rose-700"
              data-testid="confirm-delete-customer-btn"
              disabled={deleting}
              onClick={doDelete}
            >
              {deleting ? "Deleting…" : "Delete customer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
